import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    FaSprayCan, 
    FaHome, 
    FaBath, 
    FaCheckCircle, 
    FaArrowRight, 
    FaPhone, 
    FaStar, 
    FaShoppingCart, 
    FaWhatsapp,
    FaSearch,
    FaRegFolderOpen
} from 'react-icons/fa';
import { MdCleaningServices, MdKitchen } from 'react-icons/md';
import api, { API_BASE_URL } from '../utils/api';
import { products } from '../data/products';
import ProductDetailsModal from '../components/ProductDetailsModal';
import HeroSection from '../components/HeroSection';

const Home = () => {
    // Services
    const services = [
        {
            icon: <FaBath className="text-4xl" />,
            title: 'Bathroom Specialty Cleaning',
            description: 'Expert removal of hard water stains from tiles, taps, mirrors, washbasins, and water tanks.',
            color: 'from-blue-400 to-indigo-500',
        },
        {
            icon: <FaHome className="text-4xl" />,
            title: 'Floor Cleaning',
            description: 'Advanced scrubbing, polishing, and stain removal for marble, granite, tiles, and wooden floors.',
            color: 'from-cyan-400 to-teal-500',
        },
        {
            icon: <MdCleaningServices className="text-4xl" />,
            title: 'Full Home Deep Cleaning',
            description: 'Comprehensive cleaning solutions for a sparkling home.',
            color: 'from-purple-400 to-violet-500',
        },
    ];

    const whyChooseUs = [
        'Professional & Trained Staff',
        'Eco-Friendly Cleaning Products',
        'Affordable Pricing',
        '100% Satisfaction Guarantee',
        'On-Time Service',
        'All Over Hyderabad Coverage',
    ];

    const whatsappNumber = '917671842007';

    // Dynamic states
    const [heroData, setHeroData] = useState(null);
    const [productsList, setProductsList] = useState(products);
    const [categories, setCategories] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    
    // Filters & Pagination
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Modal
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch Hero Section Promo Settings
    useEffect(() => {
        const fetchHero = async () => {
            try {
                const response = await api.get('/public/hero.php');
                if (response.data && response.data.is_enabled === 1) {
                    setHeroData(response.data);
                }
            } catch (error) {
                // Silently fallback if API is not present
            }
        };
        fetchHero();
    }, []);

    // Fetch Categories
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const response = await api.get('/public/categories.php');
                if (response.data && Array.isArray(response.data)) {
                    setCategories(response.data);
                }
            } catch (error) {
                // Silently fallback
            }
        };
        fetchCats();
    }, []);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
    };

    const openModal = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <HeroSection />

            {/* Products Section */}
            <section className="section-padding bg-white" id="products">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <span className="inline-block px-4 py-2 rounded-full bg-accent-100 text-accent-700 font-semibold text-sm mb-4">
                            Our Products
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                            Premium <span className="gradient-text">Cleaning Liquids</span>
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                            Professional-grade cleaning solutions for every need
                        </p>
                    </div>

                    {/* Search & Category Filter */}
                    <div className="max-w-4xl mx-auto mb-10 flex flex-col md:flex-row gap-4 items-center justify-between bg-gray-50 p-4 rounded-3xl border border-gray-100">
                        <form onSubmit={handleSearchSubmit} className="relative w-full md:w-96">
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                            />
                            <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 h-4 w-4" />
                            <button type="submit" className="hidden">Search</button>
                        </form>

                        <div className="flex items-center gap-2 w-full md:w-auto">
                            <FaRegFolderOpen className="text-gray-400" />
                            <select
                                value={categoryFilter}
                                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                                className="w-full md:w-auto px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Product Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {products.map((product, index) => (
                            <motion.div
                                key={product.id || index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="group"
                            >
                                <div className={`bg-gradient-to-br ${product.bgColor || 'from-blue-50 to-indigo-50'} rounded-3xl p-6 h-full hover:shadow-2xl transition-all duration-300 relative flex flex-col justify-between`}>
                                    
                                    {/* Badges */}
                                    {product.isBumperOffer && (
                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-600 via-green-600 to-red-600 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg z-10 animate-bounce">
                                            🎁 FESTIVE OFFER 🎁
                                        </div>
                                    )}

                                    {product.isBestValue && !product.isBumperOffer && (
                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg z-10">
                                            🔥 Best Value!
                                        </div>
                                    )}

                                    {/* Product Image */}
                                    <div className="relative mb-6 overflow-hidden rounded-2xl bg-white/70 h-56 flex items-center justify-center border border-white/40 p-2 shadow-inner">
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="max-h-full max-w-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>

                                    {/* Product Info */}
                                    <div className="text-center flex-1 flex flex-col justify-between">
                                        <div>
                                            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider block mb-1">{product.subtitle}</span>
                                            <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                                        </div>

                                        <div>
                                            {/* Price Badge */}
                                            <div className={`inline-block px-5 py-2.5 rounded-xl bg-gradient-to-r ${product.color || 'from-primary-600 to-secondary-600'} mb-4 shadow-md`}>
                                                <span className="text-white text-2xl font-black">{product.price}</span>
                                                <span className="text-white/90 text-sm ml-1.5 font-bold">/ {product.unit}</span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="space-y-2.5">
                                                <button
                                                    onClick={() => openModal(product)}
                                                    className="inline-flex items-center justify-center w-full px-6 py-2.5 border-2 border-slate-700 text-slate-800 hover:bg-slate-950 hover:text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 text-sm"
                                                >
                                                    View Details
                                                </button>
                                                <a
                                                    href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi! I want to order ${product.name} (${product.price} / ${product.unit}). Please confirm availability.`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`inline-flex items-center justify-center w-full px-6 py-2.5 bg-gradient-to-r ${product.color || 'from-primary-600 to-secondary-600'} text-white font-bold rounded-full hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-sm`}
                                                >
                                                    <FaShoppingCart className="mr-2" /> Order Now
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-4 mt-12">
                                    <button
                                        disabled={page === 1}
                                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                        className="px-4 py-2 border border-gray-200 rounded-xl disabled:opacity-50 text-sm font-semibold hover:bg-gray-100 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm font-medium text-gray-700">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        disabled={page === totalPages}
                                        onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                                        className="px-4 py-2 border border-gray-200 rounded-xl disabled:opacity-50 text-sm font-semibold hover:bg-gray-100 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                </div>
            </section>

            {/* Services Section */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm mb-4">
                            Our Services
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                            What We <span className="gradient-text">Offer</span>
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                            Professional cleaning solutions tailored to your needs
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.15 }}
                                className="group"
                            >
                                <div className="card p-8 h-full hover:shadow-2xl transition-all duration-300">
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                    <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                                    <Link to="/services" className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors">
                                        Learn More <FaArrowRight className="ml-2 text-sm" />
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="section-padding relative overflow-hidden">
                <div className="absolute inset-0 gradient-bg opacity-95" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />

                <div className="relative container-custom">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white font-semibold text-sm mb-4">
                                Why Choose Us
                            </span>
                            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                                The Best Cleaning Service in Hyderabad
                            </h2>
                            <p className="text-white/80 text-lg mb-8 leading-relaxed">
                                With years of experience and a commitment to excellence, we deliver cleaning services
                                that transform your space into a spotless sanctuary.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {whyChooseUs.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-3"
                                    >
                                        <FaCheckCircle className="text-accent-400 text-xl flex-shrink-0" />
                                        <span className="text-white font-medium">{item}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        <div className="relative">
                            <div className="glass-card p-8 text-center">
                                <FaSprayCan className="text-6xl text-accent-400 mx-auto mb-6" />
                                <h3 className="text-2xl font-bold text-white mb-4">Get a Free Quote</h3>
                                <p className="text-white/70 mb-6">Contact us today for a personalized cleaning solution</p>
                                <Link to="/contact" className="btn-accent w-full">
                                    Contact Us Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Details Modal */}
            <ProductDetailsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                product={selectedProduct}
            />
        </div>
    );
};

export default Home;
